
import type { Dispatch } from "redux";
import
{
  DRAW_DRAWING_FEATURE_COLOR,
  DRAW_DRAWING_FEATURE_STROKE_COLOR,
  DRAW_DRAWING_FEATURE_STROKE_WIDTH,
  DRAW_FEATURE_COLOR,
  DRAW_FEATURE_KEY,
  DRAW_FEATURE_STROKE_COLOR,
  DRAW_FEATURE_STROKE_WIDTH,
  DRAW_LAYER_ID,
  DRAW_MODIFY_FEATURE_COLOR,
  DRAW_MODIFY_FEATURE_STROKE_COLOR,
  DRAW_MODIFY_FEATURE_STROKE_WIDTH,
  DRAW_SOURCE_ID,
  EDIT_SOURCE_ID,
} from "../consts";
import type { Coordinate, DrawFeature, DrawGeometry, DrawingMode } from "../types";
import type { UnknownAction } from "redux";
import { addFeature, setDrawingMode, updateFeature } from "../../../store/slices/drawSlice";


// ── Types ─────────────────────────────────────────────────────────────────────

type MLMap = {
  on: (ev: string, h: (e: unknown) => void) => void;
  off: (ev: string, h: (e: unknown) => void) => void;
  remove: () => void;
  addSource: (id: string, spec: object) => void;
  removeSource: (id: string) => void;
  getSource: (id: string) => undefined | { setData(d: object): void };
  addLayer: (spec: object, before?: string) => void;
  removeLayer: (id: string) => void;
  getLayer: (id: string) => unknown;
  setLayoutProperty: (id: string, k: string, v: unknown) => void;
  setPaintProperty: (id: string, k: string, v: unknown) => void;
  getCenter: () => { lat: number; lng: number };
  getZoom: () => number;
  getBearing: () => number;
  getPitch: () => number;
  dragPan: { enable(): void; disable(): void };
  queryRenderedFeatures: (pt: unknown, opts: { layers: string[] }) => { properties: Record<string, unknown> }[];
  doubleClickZoom: { enable(): void; disable(): void };
  getCanvas: () => { style: CSSStyleDeclaration };
  isStyleLoaded: () => boolean;
  once: (type: string, listener?: (ev: unknown) => void) => Promise<unknown>;
};

type LngLatEvent = { lngLat: { lng: number; lat: number } };
type PointEvent = { point: unknown };

interface MapDrawState
{
  map: MLMap | null;
  mode: DrawingMode;
  dead: boolean;
  ready: boolean;
  addedIds: Set<string>;
  drawPts: [number, number][];
  mousePos: Coordinate | null;
  livePoints: Coordinate[];
  rectStart: [number, number] | null;
  rectEnd: [number, number] | null;
  moveEndH: ((e: unknown) => void) | null;
  clickH: ((e: unknown) => void) | null;
  dblH: ((e: unknown) => void) | null;
  mdH: ((e: unknown) => void) | null;
  mmH: ((e: unknown) => void) | null;
  muH: ((e: unknown) => void) | null;
  mClickH: ((e: unknown) => void) | null;
  mDblH: ((e: unknown) => void) | null;
  mMoveH: ((e: unknown) => void) | null;
  mEditClickH: ((e: unknown) => void) | null;
  mEditMoveH: ((e: unknown) => void) | null;
  mEditUpH: ((e: unknown) => void) | null;
  lastClickMs: number;
  editDrag: { segId: string; vIdx: number } | null;
  dragIdx: number;
  dragId: string | null;
  dragGeom: DrawGeometry | null;
  editingFeatureId: string | null;
  features: DrawFeature[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Extract [lng, lat] from a map event */
const toLngLat = (e: unknown): Coordinate =>
{
  const ev = e as LngLatEvent;
  return [ev.lngLat.lng, ev.lngLat.lat];
};

/** Ensure polygon ring is closed (last point === first point) */
const closePolygon = (coords: Coordinate[]): Coordinate[] =>
{
  if (coords.length < 2) return coords;
  const first = coords[0],
    last = coords[coords.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? coords : [...coords, first];
};

/** Strip closing duplicate from polygon ring */
const openPolygon = (coords: Coordinate[]): Coordinate[] =>
{
  if (coords.length < 2) return coords;
  const first = coords[0],
    last = coords[coords.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? coords.slice(0, -1) : coords;
};

/** Build feature GeoJSON list (non-preview features) */
const buildExistingFeats = (features: DrawFeature[]): object[] =>
  features
    .filter((f: any) => !f.preview)
    .map((f: any) => ({
      ...f,
      properties: { ...f.properties, color: f.fill || DRAW_FEATURE_COLOR },
    }));

const newFeatureId = () => `${DRAW_FEATURE_KEY}-${crypto.randomUUID()}`;

const clearState = (S: MapDrawState) =>
{
  S.drawPts = [];
  S.dragIdx = -1;
  S.dragId = null;
  S.dragGeom = null;
  S.mousePos = null;
  S.livePoints = [];
  S.rectStart = null;
  S.rectEnd = null;
};

// ── Main factory ──────────────────────────────────────────────────────────────

export function createMapLibreDrawing(
  map: MLMap,
  mode: DrawingMode,
  editingFeatureId: string | null,
  features: DrawFeature[],
  dispatch: Dispatch<UnknownAction>,
  allowMultiple = false,
)
{
  const S: MapDrawState = {
    map,
    mode,
    dead: false,
    ready: false,
    addedIds: new Set(),
    drawPts: [],
    mousePos: null,
    livePoints: [],
    rectStart: null,
    rectEnd: null,
    moveEndH: null,
    clickH: null,
    dblH: null,
    mdH: null,
    mmH: null,
    muH: null,
    mClickH: null,
    mDblH: null,
    mMoveH: null,
    mEditClickH: null,
    mEditMoveH: null,
    mEditUpH: null,
    lastClickMs: 0,
    editDrag: null,
    dragIdx: -1,
    dragId: null,
    dragGeom: null,
    editingFeatureId,
    features: features ?? [],
  };

  /** Finish drawing: dispatch, optionally reset, update map */
  const finishFeature = (feature: DrawFeature, newFeatures: DrawFeature[]) =>
  {
    dispatch(addFeature(feature));
    if (allowMultiple)
    {
      resetTool(S);
      pushDraw(S, newFeatures);
    } else
    {
      removeTool(S);
      dispatch(setDrawingMode("NONE"));
      pushDraw(S, newFeatures);
    }
  };

  return {
    activate()
    {
      initDrawLayers(S);
      if (mode === "NONE") return;
      console.log('draw', features, mode);
      // ── EDIT mode: drag vertices ────────────────────────────────────────────
      if (mode === "EDIT")
      {
        const mdH = (e: unknown) =>
        {
          const ev = e as PointEvent;
          const hits = map.queryRenderedFeatures(ev.point, { layers: [`${DRAW_LAYER_ID}_edit-vertex`] });
          if (!hits.length) return;

          map.getCanvas().style.cursor = "move";
          S.dragIdx = Number(hits[0].properties["vertexIdx"]);
          S.dragId = String(hits[0].properties["featId"]);
          S.dragGeom = S.features.find((f) => f.id === S.dragId)?.geometry ?? null;
          map.dragPan.disable();
        };

        const mmH = (e: unknown) =>
        {
          if (S.dragIdx < 0 || !S.dragId || !S.dragGeom) return;
          const coord = toLngLat(e);

          // Normalize polygon: remove closing duplicate before patching
          let geom = S.dragGeom;
          if (geom.type === "Polygon")
          {
            const open = openPolygon(geom.coordinates[0]);
            geom = { type: "Polygon", coordinates: [open] };
          }

          const ng = patchGeom(geom, S.dragIdx, coord);
          S.dragGeom = ng;

          // Re-close polygon for rendering/storage
          const storageGeom: DrawGeometry = ng.type === "Polygon" ? { type: "Polygon", coordinates: [closePolygon(ng.coordinates[0])] } : ng;

          dispatch(updateFeature({ id: S.dragId, geometry: storageGeom }));
          const updated = S.features.map((f) => (f.id === S.dragId ? { ...f, geometry: storageGeom } : f));
          pushDraw(S, updated);
        };

        const muH = () =>
        {
          map.getCanvas().style.cursor = "";
          S.dragIdx = -1;
          S.dragId = null;
          S.dragGeom = null;
          map.dragPan.enable();
        };

        map.on("mousedown", mdH);
        map.on("mousemove", mmH);
        map.on("mouseup", muH);
        S.mdH = mdH;
        S.mmH = mmH;
        S.muH = muH;
        return;
      }

      map.doubleClickZoom.disable();
      if (mode != "VIEW")
        map.getCanvas().style.cursor = "crosshair";
      // ── POINT mode ─────────────────────────────────────────────────────────
      if (mode === "POINT")
      {
        const clickH = (e: unknown) =>
        {
          const coords = toLngLat(e);
          const feature: DrawFeature = {
            id: newFeatureId(),
            geometry: { type: "Point", coordinates: coords },
            label: "",
            createdAt: Date.now(),
          };
          const newFeatures = [...S.features, feature];
          finishFeature(feature, newFeatures);
          if (allowMultiple)
          {
            map.getSource(DRAW_SOURCE_ID)?.setData({
              type: "FeatureCollection",
              features: [...buildExistingFeats(S.features), { ...feature, properties: { color: (feature as any).fill || DRAW_FEATURE_COLOR } }],
            });
          }
        };

        const mMoveH = (e: unknown) =>
        {
          S.mousePos = toLngLat(e);
          map.getSource(DRAW_SOURCE_ID)?.setData({
            type: "FeatureCollection",
            features: [...buildExistingFeats(S.features), { type: "Feature", geometry: { type: "Point", coordinates: S.mousePos } }],
          });
        };

        map.on("click", clickH);
        map.on("mousemove", mMoveH);
        S.clickH = clickH;
        S.mMoveH = mMoveH;
        return;
      }

      // ── RECTANGLE mode: two-click corner-to-corner ─────────────────────────
      if (mode === "RECTANGLE")
      {
        const mclickH = (e: unknown) =>
        {
          const coord = toLngLat(e) as [number, number];
          S.drawPts.push(coord);
          S.livePoints = S.drawPts;

          if (!S.rectStart)
          {
            S.rectStart = coord;
            return;
          }

          S.rectEnd = coord;
          const ring = rectRing(S.rectStart, S.rectEnd);
          const coords = ring.slice(0, -1) as [number, number][];
          const feature: DrawFeature = {
            id: newFeatureId(),
            geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] },
            label: "",
            createdAt: Date.now(),
          };
          S.rectStart = null;
          S.rectEnd = null;
          map.dragPan.enable();
          finishFeature(feature, [...S.features, feature]);
        };

        const mmH = (e: unknown) =>
        {
          if (!S.rectStart || !S.livePoints?.length) return;
          const coord = toLngLat(e) as [number, number];
          S.mousePos = coord;
          S.rectEnd = coord;
          const ring = rectRing(S.rectStart, S.rectEnd);
          const coords = ring.slice(0, -1) as [number, number][];
          map.getSource(DRAW_SOURCE_ID)?.setData({
            type: "FeatureCollection",
            features: [
              ...buildExistingFeats(S.features),
              {
                id: newFeatureId(),
                geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] },
                label: "",
                createdAt: Date.now(),
              },
              { type: "Feature", geometry: { type: "Point", coordinates: S.mousePos } },
            ],
          });
        };

        map.on("click", mclickH);
        map.on("mousemove", mmH);
        S.mClickH = mclickH;
        S.mmH = mmH;
        return;
      }

      // ── LINESTRING / POLYGON: click to add points, dblclick to finish ───────
      const clickH = (e: unknown) =>
      {
        S.drawPts.push(toLngLat(e) as [number, number]);
        S.livePoints = S.drawPts;
        pushDraw(S, S.features);
      };

      const dblH = () =>
      {
        const pts = S.drawPts.slice(0, -1) as Coordinate[];
        let feature: DrawFeature | null = null;

        if (mode === "LINESTRING" && pts.length >= 2)
        {
          feature = {
            id: newFeatureId(),
            geometry: { type: "LineString", coordinates: pts },
            label: "",
            createdAt: Date.now(),
          };
        } else if (mode === "POLYGON" && pts.length >= 3)
        {
          feature = {
            id: newFeatureId(),
            geometry: { type: "Polygon", coordinates: [[...pts, pts[0]]] },
            label: "",
            createdAt: Date.now(),
          };
        }

        if (feature) finishFeature(feature, [...S.features, feature]);
      };

      const mMoveH = (e: unknown) =>
      {
        S.mousePos = toLngLat(e);
        if (S.livePoints?.length) pushDrawLive(S);
      };

      map.on("click", clickH);
      map.on("dblclick", dblH);
      map.on("mousemove", mMoveH);
      S.clickH = clickH;
      S.dblH = dblH;
      S.mMoveH = mMoveH;
    },

    deactivate()
    {
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = "";
      S.dead = true;
      S.ready = false;

      removeTool(S);

      try
      {
        if (S.moveEndH) map.off("moveend", S.moveEndH);
        if (S.clickH) map.off("mouseup", S.clickH);
        if (S.dblH) map.off("dblclick", S.dblH);
        if (S.mdH) map.off("mousedown", S.mdH);
        if (S.mmH) map.off("mousemove", S.mmH);
        if (S.muH) map.off("mouseup", S.muH);
        if (S.mClickH) map.off("click", S.mClickH);
        if (S.mDblH) map.off("dblclick", S.mDblH);
        if (S.mMoveH) map.off("mousemove", S.mMoveH);
        if (S.mEditClickH) map.off("click", S.mEditClickH);
        if (S.mEditMoveH) map.off("mousemove", S.mEditMoveH);
        if (S.mEditUpH) map.off("mouseup", S.mEditUpH);
        detachToolHandlers(map, S);
      } catch
      {
        /* ignore — already removed or container gone */
      }

      S.map = null;
      S.moveEndH = null;
      clearState(S);

      try
      {
        map.dragPan.enable();
      } catch
      {
        /* */
      }
    },
  };
}

// ── Layer / source initialisation ─────────────────────────────────────────────

const initDrawLayers = (S: MapDrawState) =>
{
  const { map, features } = S;
  if (!map) return;

  // avoid calls before style is ready
  if (typeof map.isStyleLoaded === "function" && !map.isStyleLoaded())
  {
    const onceKey = "__drawLayersPending";
    if (!(map as any)[onceKey])
    {
      (map as any)[onceKey] = true;
      map.once("styledata", () =>
      {
        (map as any)[onceKey] = false;
        initDrawLayers(S);
      });
    }
    return;
  }

  try
  {
    const drawData = drawGJ(S, features);
    const editData = editGJ(S.editingFeatureId, features);

    const sourceDraw = map.getSource(DRAW_SOURCE_ID);
    if (!sourceDraw) map.addSource(DRAW_SOURCE_ID, { type: "geojson", data: drawData });
    else sourceDraw.setData(drawData);

    const sourceEdit = map.getSource(EDIT_SOURCE_ID);
    if (!sourceEdit) map.addSource(EDIT_SOURCE_ID, { type: "geojson", data: editData });
    else sourceEdit.setData(editData);

    const addLayerIfMissing = (spec: object) =>
    {
      const id = (spec as { id: string }).id;
      if (!map.getLayer(id)) map.addLayer(spec);
    };

    addLayerIfMissing({
      id: `${DRAW_LAYER_ID}_draw-fill`,
      type: "fill",
      source: DRAW_SOURCE_ID,
      paint: { "fill-color": ["coalesce", ["get", "color"], DRAW_DRAWING_FEATURE_COLOR], "fill-opacity": 0.18 },
      filter: ["==", ["geometry-type"], "Polygon"],
    });

    addLayerIfMissing({
      id: `${DRAW_LAYER_ID}_draw-line`,
      type: "line",
      source: DRAW_SOURCE_ID,
      paint: {

        "line-color": ["case", ["has", "stroke_color"], ["get", "stroke_color"], ["has", "stroke"], ["get", "stroke"], DRAW_DRAWING_FEATURE_STROKE_COLOR],

        // "line-color": ["coalesce", ["get", "stroke_color"], DRAW_DRAWING_FEATURE_STROKE_COLOR], "line-width": DRAW_DRAWING_FEATURE_STROKE_WIDTH
      },
      filter: ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
    });

    addLayerIfMissing({
      id: `${DRAW_LAYER_ID}_draw-preview`,
      type: "line",
      source: DRAW_SOURCE_ID,
      paint: { "line-color": DRAW_DRAWING_FEATURE_STROKE_COLOR, "line-width": DRAW_DRAWING_FEATURE_STROKE_WIDTH, "line-dasharray": [4, 3] },
      filter: ["all", ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]], ["==", ["get", "preview"], true]],
    });

    addLayerIfMissing({
      id: `${DRAW_LAYER_ID}_draw-point`,
      type: "circle",
      source: DRAW_SOURCE_ID,
      paint: {
        "circle-radius": 5,
        "circle-color": ["coalesce", ["get", "color"], DRAW_DRAWING_FEATURE_COLOR],
        "circle-stroke-width": DRAW_DRAWING_FEATURE_STROKE_WIDTH,
        "circle-stroke-color": DRAW_DRAWING_FEATURE_STROKE_COLOR,
      },
      filter: ["==", ["geometry-type"], "Point"],
    });

    addLayerIfMissing({
      id: `${DRAW_LAYER_ID}_edit-vertex`,
      type: "circle",
      source: EDIT_SOURCE_ID,
      paint: {
        "circle-radius": 5,
        "circle-color": DRAW_MODIFY_FEATURE_COLOR,
        "circle-stroke-width": DRAW_MODIFY_FEATURE_STROKE_WIDTH,
        "circle-stroke-color": DRAW_MODIFY_FEATURE_STROKE_COLOR,
      },
    });
  } catch
  {
    /* ignore */
  }
};

// ── Live preview while drawing ────────────────────────────────────────────────

const pushDrawLive = (S: MapDrawState) =>
{
  const { map: m, livePoints: pts, mousePos: cur, mode } = S;
  if (!m) return;

  const features: object[] = buildExistingFeats(S.features);
  const preview = cur && pts.length > 0 ? [...pts, cur] : pts;

  if (preview.length >= 2)
  {
    // const isPolygon = mode === "POLYGON";
    const isPolygon = mode === "POLYGON" && preview.length > 2;
    features.push({
      type: "Feature",
      geometry: {
        type: isPolygon ? "Polygon" : "LineString",
        coordinates: isPolygon ? [[...preview, preview[0]]] : preview,
      },
      properties: { preview: true },
    });
    if (cur)
    {
      features.push({ type: "Feature", geometry: { type: "Point", coordinates: cur } });
    }
  }

  m.getSource(DRAW_SOURCE_ID)?.setData({ type: "FeatureCollection", features });
};

// ── Sync draw + edit sources ──────────────────────────────────────────────────

const pushDraw = (S: MapDrawState, features: DrawFeature[]) =>
{
  const m = S.map;
  if (!m) return;
  S.features = features ?? [];
  m.getSource(DRAW_SOURCE_ID)?.setData(drawGJ(S, features));
  m.getSource(EDIT_SOURCE_ID)?.setData(editGJ(S.editingFeatureId, features));
};

// ── GeoJSON builders ──────────────────────────────────────────────────────────

const rectRing = (a: [number, number], b: [number, number]): [number, number][] => [
  [a[0], a[1]],
  [b[0], a[1]],
  [b[0], b[1]],
  [a[0], b[1]],
  [a[0], a[1]],
];

const drawGJ = (S: MapDrawState, features: DrawFeature[]) =>
{
  const pts = S.drawPts ?? [];
  const mode = S.mode;
  const feats: object[] = features.map((f) =>
  {
    let geometry = f.geometry;
    if (geometry.type === "Polygon")
    {
      const coords = geometry.coordinates[0];
      geometry = { type: "Polygon", coordinates: [closePolygon(coords)] };
    }
    return {
      type: "Feature",
      id: f.id,
      // properties: { color: (f as any).fill || DRAW_FEATURE_COLOR },
      properties: {
        color: (f as any).fill || DRAW_FEATURE_COLOR,
        fill: (f as any).fill || DRAW_FEATURE_COLOR,
        stroke: (f as any).stroke || DRAW_FEATURE_STROKE_COLOR,
        stroke_color: (f as any).stroke || DRAW_FEATURE_STROKE_COLOR,
        stroke_width: (f as any).strokeWidth || DRAW_FEATURE_STROKE_WIDTH,
      },

      geometry,
    };
  });

  if (pts.length >= 2)
  {
    feats.push({
      type: "Feature",
      properties: { preview: true, color: DRAW_DRAWING_FEATURE_COLOR },
      geometry: {
        type: mode === "LINESTRING" ? "LineString" : "Polygon",
        coordinates: mode === "LINESTRING" ? pts : [[...pts, pts[0]]],
      },
    });
  }

  return { type: "FeatureCollection", features: feats };
};

const editGJ = (featureId: string | null, features: DrawFeature[]) =>
{
  const feat = features.find((f) => f.id === featureId);
  if (!feat) return { type: "FeatureCollection", features: [] };

  let verts: Coordinate[] =
    feat.geometry.type === "Point"
      ? [feat.geometry.coordinates]
      : feat.geometry.type === "LineString"
        ? feat.geometry.coordinates
        : feat.geometry.coordinates[0];

  verts = openPolygon(verts);

  return {
    type: "FeatureCollection",
    features: verts.map((c, i) => ({
      type: "Feature",
      properties: { vertexIdx: i, featId: featureId },
      geometry: { type: "Point", coordinates: c },
    })),
  };
};

// ── State management ──────────────────────────────────────────────────────────

function resetTool(S: MapDrawState)
{
  clearState(S);
  S.features = [];
}

function removeTool(S: MapDrawState)
{
  if (S.map) detachToolHandlers(S.map, S);
  resetTool(S);
}

function detachToolHandlers(m: MLMap, S: MapDrawState)
{
  if (S.clickH) m.off("click", S.clickH);
  if (S.dblH) m.off("dblclick", S.dblH);
  if (S.mdH) m.off("mousedown", S.mdH);
  if (S.mmH) m.off("mousemove", S.mmH);
  if (S.muH) m.off("mouseup", S.muH);
  S.clickH = S.dblH = null;
  S.mdH = S.mmH = S.muH = null;
  try
  {
    m.dragPan.enable();
  } catch
  {
    /* */
  }
}

function patchGeom(g: DrawGeometry, idx: number, c: Coordinate): DrawGeometry
{
  if (g.type === "Point") return { type: "Point", coordinates: c };
  if (g.type === "LineString") return { type: "LineString", coordinates: g.coordinates.map((x, i) => (i === idx ? c : x)) };
  return { type: "Polygon", coordinates: [g.coordinates[0].map((x, i) => (i === idx ? c : x))] };
}
